import { lazy, Suspense, useEffect, useLayoutEffect, useRef } from 'react'
import { Routes, Route, useLocation, useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { setTokenGetter } from './api/client'
import { trackSpaPageview } from './analytics/posthog'
import { UI_LOCALES } from './i18n/config'
import { IndexPage } from './pages/IndexPage'
import { CookieConsentBanner } from './components/consent/CookieConsentBanner'

// The landing is the SEO page and stays in the main bundle; service pages
// load on demand so they don't weigh down the first paint
const ConvertPage = lazy(() =>
  import('./pages/ConvertPage').then((m) => ({ default: m.ConvertPage })),
)
const HistoryPage = lazy(() =>
  import('./pages/HistoryPage').then((m) => ({ default: m.HistoryPage })),
)
const AdminPage = lazy(() =>
  import('./pages/AdminPage').then((m) => ({ default: m.AdminPage })),
)
const NotFoundPage = lazy(() =>
  import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
)

/** /ru/, /de/, /zh-hans/ … — the locale homes are the landing itself (static
 * localized shells boot the app here; I18nProvider picks the language up
 * from the path). Anything else single-segment falls through to the 404,
 * which redirects bare non-UI locale codes into /?lang=. */
function LocaleHome() {
  const { locale } = useParams()
  if (!locale || !UI_LOCALES.some((l) => l.code === locale)) {
    return <NotFoundPage />
  }
  return <IndexPage />
}

/** posthog.init fires the first $pageview; SPA route changes fire the rest —
 * without this /history and /admin never show up as pageviews. */
function SpaPageviews() {
  const { pathname } = useLocation()
  const first = useRef(true)
  useEffect(() => {
    if (first.current) {
      first.current = false
      return
    }
    trackSpaPageview()
  }, [pathname])
  return null
}

/** Wires the API client to Auth0 on every route — before, only the landing
 * did this, so direct visits to other pages made unauthenticated calls. */
function AuthTokenBridge() {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0()
  useLayoutEffect(() => {
    if (isAuthenticated) {
      setTokenGetter(getAccessTokenSilently)
    }
  }, [isAuthenticated, getAccessTokenSilently])
  return null
}

export default function App() {
  return (
    <>
      <AuthTokenBridge />
      <SpaPageviews />
      <CookieConsentBanner />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<IndexPage />} />
          <Route path="/history" element={<HistoryPage />} />
          {/* The indexed page is the static /subtitle-converter/ landing;
              this is the tool it links to */}
          <Route path="/convert" element={<ConvertPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/:locale" element={<LocaleHome />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </>
  )
}
